

import React, { useState, useEffect, useRef,useCallback  } from 'react';
import axiosClient from '../../../../api/axiosClient';
import styles from './CustomerDetail.module.css';
import { customerApi } from '../../../../api/customer/customerApi';
import SalesCogsFilters from '../../Home/SalesCogsFilters/SalesCogsFilters'; // adjust path
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Chart } from 'react-chartjs-2';

const getCurrentFY = () => {
  const now = new Date();
  const month = now.getMonth() + 1;
  const year = now.getFullYear();
  const startYear = month >= 4 ? year : year - 1;
  return `${startYear}-${String(startYear + 1).slice(2)}`;
};

const buildFYOptions = () => {
  const current = new Date().getFullYear();
  const options = [];
  for (let y = current; y >= 2022; y--) {
    options.push(`${y}-${String(y + 1).slice(2)}`);
  }
  return options;
};

ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);


const CustomerDetail = ({ initialCustomerCode, onBack }) => {
  // Category color mapping
  const categoryColors = {
    "3A Chemicals": "#4E79A7",
    "Amino Acids": "#F28E2B",
    "Analytical Instruments": "#E15759",
    "Analytical Standards": "#76B7B2",
    "API": "#59A14F",
    "Assets": "#EDC948",
    "Biochemicals": "#B07AA1",
    "Biological Buffers": "#FF9DA7",
    "British Pharmacopoeia": "#9C755F",
    "Building Blocks": "#BAB0AC",
    "Cans": "#17BECF",
    "Capricorn": "#BCBD22",
    "Carbohydrates": "#393B79",
    "Catalyst": "#5254A3",
    "Cell Culture": "#6B6ECF",
    "Chinese Pharmacopeia": "#4E79A7",
    "Cylinders": "#9C9EDE",
    "Dyes": "#637939",
    "Enzyme": "#8CA252",
    "EP Standards": "#B5CF6B",
    "Equipment and Instruments": "#CEDB9C",
    "Fine Chemicals": "#8C6D31",
    "Fixed Assets": "#BD9E39",
    "Food Grade": "#BD9E39",
    "Gas Chromatography": "#E7BA52",
    "GC Consumable": "#843C39",
    "Glucuronides": "#E7BA52",
    "High Purity Acids": "#843C39",
    "HPLC configurations": "#AD494A",
    "HPLC consumables": "#D6616B",
    "Impurity": "#AD494A",
    "Indian pharmacopoeia": "#D6616B",
    "Instruments": "#E7969C",
    "Intermediates": "#7B4173",
    "Items": "#A55194",
    "Kanto": "#CE6DBD",
    "Lab Consumables": "#CE6DBD",
    "Lab Systems & Fixtures": "#DE9ED6",
    "Label": "#3182BD",
    "Laboratory Containers & Storage": "#3182BD",
    "Life science": "#6BAED6",
    "Ligand": "#9ECAE1",
    "Meatls&materials": "#E6550D",
    "Membranes": "#6BAED6",
    "Metabolites": "#9ECAE1",
    "Metal Standard Solutions": "#E6550D",
    "Multiple Pharmacopoeia": "#FD8D3C",
    "Natural Products": "#FDAE6B",
    "New Life Biologics": "#FDD0A2",
    "Nitrosamine": "#31A354",
    "NMR Solvents": "#74C476",
    "Nucleosides and Nucleotides": "#A1D99B",
    "Packaging Materials": "#C7E9C0",
    "Peptides": "#756BB1",
    "Pesticide Standards": "#9E9AC8",
    "Polymer": "#BCBDDC",
    "Reagent": "#DADAEB",
    "Reference Materials": "#636363",
    "Secondary Standards": "#969696",
    "Services": "#BDBDBD",
    "Solvent": "#D9D9D9",
    "Stable Isotope reagents": "#8DD3C7",
    "Stable isotopes": "#FFFFB3",
    "Sustaintech": "#BEBADA",
    "Trading": "#BEBADA",
    "Ultrapur": "#FB8072",
    "Ultrapur-100": "#80B1D3",
    "USP Standards": "#FDB462",
    "VOLAB": "#FD8D3C",
    "Uncategorized": "#9ca3af"
  };

  // State management
  const [customerCode, setCustomerCode] = useState(initialCustomerCode || '');
  const [searchInput, setSearchInput] = useState('');
  const [showDropdown, setShowDropdown] = useState(false);
  const [suggestions, setSuggestions] = useState([]);
  const [loadingSuggestions, setLoadingSuggestions] = useState(false);
  
  const [customerDetails, setCustomerDetails] = useState(null);
  const [salesCogsData, setSalesCogsData] = useState([]);
  const [outstandingData, setOutstandingData] = useState([]);
  const [categorySalesData, setCategorySalesData] = useState([]);
  const [userRole, setUserRole] = useState(3);

  const [categoryFY, setCategoryFY] = useState(getCurrentFY());
  
  const [salesYear, setSalesYear] = useState(new Date().getFullYear());
  const [categoryYear, setCategoryYear] = useState(new Date().getFullYear());

  const [salesFY,          setSalesFY]          = useState(getCurrentFY());
  const [salesActiveFilters, setSalesActiveFilters] = useState({});

  
  const fyOptions = buildFYOptions();

  const [selectedInvoices,  setSelectedInvoices]  = useState([]);
  const [isMailSending,     setIsMailSending]      = useState(false);
  const [outstandingFilter, setOutstandingFilter]  = useState('Payment Pending');

  // Outstanding balance pagination and search
  const [outstandingPage, setOutstandingPage] = useState(0);
  const [outstandingSearch, setOutstandingSearch] = useState('');
  const outstandingItemsPerPage = 15;
  
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  
  const dropdownRef = useRef(null);
  const searchTimeoutRef = useRef(null);

  // Fetch customer data when customerCode changes
  useEffect(() => {
    if (customerCode) {
      fetchCustomerData();
    }
  }, [customerCode]);

  // Fetch sales/COGS when year changes
  useEffect(() => {
    if (customerCode) {
      fetchSalesCogs();
    }
  }, [customerCode, salesYear]);

  // Fetch category sales when year changes
  useEffect(() => {
    if (customerCode) {
      fetchCategorySales();
    }
  }, [customerCode, categoryYear]);

  // Handle search input changes with debounce
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchInput.trim().length >= 2) {
      searchTimeoutRef.current = setTimeout(() => {
        fetchSuggestions(searchInput.trim());
      }, 300);
    } else {
      setSuggestions([]);
      setShowDropdown(false);
    }

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchInput]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  const handleMailSend = async () => {
  if (selectedInvoices.length === 0) {
    alert('Please select at least one invoice to mail');
    return;
  }

  try {
    setIsMailSending(true);

    // Get all outstanding to filter selected rows
    const allData = outstandingData.filter(item =>
      selectedInvoices.includes(item.invoiceNo)
    );

    const formatCur = (n) => '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 });
    const formatDt  = (d) => d ? new Date(d).toLocaleDateString('en-GB') : 'N/A';

    // Sort by overdue days desc
    allData.sort((a, b) => (b.overdueDays || 0) - (a.overdueDays || 0));

    const tableRows = allData.map(row => `
      <tr>
        <td style="text-align:center;">${row.invoiceNo || 'N/A'}</td>
        <td style="text-align:center;">${formatDt(row.invoiceDate)}</td>
        <td style="text-align:center;">${customerDetails.customerName || 'N/A'}</td>
        <td style="text-align:center;">${row.soCustomerRefNo || 'N/A'}</td>
        <td style="text-align:center;">${formatCur(row.invoiceTotal)}</td>
        <td style="text-align:center;">${formatCur(row.balanceDue)}</td>
        <td style="text-align:center;">${row.overdueDays > 0 ? row.overdueDays : ''}</td>
        <td style="text-align:center;">${row.trackingNo || 'N/A'}</td>
        <td style="text-align:center;">${formatDt(row.dispatchDate)}</td>
        <td style="text-align:center;">${row.salesPerson || 'N/A'}</td>
      </tr>`).join('');

    const totalInvoice = allData.reduce((s, r) => s + (r.invoiceTotal || 0), 0);
    const totalBalance = allData.reduce((s, r) => s + ((r.overdueDays > 0 ? r.balanceDue : 0) || 0), 0);

    const body = `
      <div style="font-family:Arial,sans-serif;line-height:1.6;">
        <p>Dear Sir / Madam,</p>
        <p>Greetings of the day!</p>
        <p>Kindly find below the list of outstanding invoices currently showing as unpaid in our accounts.</p>
        <p>We request you to please verify whether all these invoices have been recorded in your books,
           and arrange to make the payment for the due bills as per the agreed credit terms.</p>
        <p><span style="color:red;text-decoration:underline;">
          If the payment has already been made, please disregard this message. Otherwise,
          we would appreciate it if you could process the payment at your earliest convenience.
        </span></p>
        <p>The <strong>total outstanding amount</strong> is <strong>${formatCur(totalInvoice)}</strong>,
           out of which <strong>${formatCur(totalBalance)}</strong> is <strong>overdue for payment.</strong></p>
        <table border="1" cellpadding="6" cellspacing="0" style="border-collapse:collapse;width:100%;">
          <thead><tr>
            <th>Invoice No.</th><th>Invoice Date</th><th>Customer Name</th>
            <th>SO Customer Ref. No</th><th>Invoice Total</th><th>Balance Due</th>
            <th>Overdue Days</th><th>Tracking no</th><th>Dispatch Date</th><th>Sales Person</th>
          </tr></thead>
          <tbody>${tableRows}</tbody>
        </table>
        <p>Looking forward to your confirmation.</p>
        <p>Regards,<br/>Shafique Khan<br/>Manager - Accounts<br/><br/>
           <strong>Website: www.densitypharmachem.com</strong><br/><br/>
           DENSITY PHARMACHEM PRIVATE LIMITED<br/><br/>
           Sy No 615/A & 624/2/1, Pudur Village<br/>
           Medchal-Malkajgiri District,<br/>
           Hyderabad, Telangana, India-501401<br/>
           Mobile: +91-9029298654<br/><br/>
           <strong>Bank Details</strong><br/>
           Name: Density Pharmachem Private Limited<br/>
           Bank Name: HDFC Bank Ltd<br/>
           Branch: Hyderguda<br/>
           Account Number: 99999989991174<br/>
           IFSC Code: HDFC0001996
        </p>
        <p style="color:red;font-weight:bold;">
          GST Number: 36AAKCD9426G1ZE<br/>
          MSME Number: UDYAM-TS-20-0101328
        </p>
      </div>`;

    const salesPersonEmail = customerDetails?.salesPersonEmail; // add to detail query if needed
    const toEmail          = customerDetails?.email;

    if (!toEmail) { alert('Customer email not found'); return; }

    const res = await axiosClient.post('/email/send', {
      from:    'shafique@densitypharmachem.com',
      to:      [toEmail],
      cc:      salesPersonEmail ? [salesPersonEmail] : [],
      subject: 'Request for Confirmation and Payment of Outstanding Invoices',
      body,
    });

    alert('Email sent successfully');
    setSelectedInvoices([]);
  } catch (err) {
    console.error('Error sending email:', err);
    alert('Error sending email');
  } finally {
    setIsMailSending(false);
  }
};


  // Fetch customer suggestions
  const fetchSuggestions = async (searchTerm) => {
    try {
      setLoadingSuggestions(true);
      const data = await customerApi.getCustomers(0, 10, searchTerm);
      setSuggestions(data.customers || []);
      setShowDropdown(true);
    } catch (err) {
      console.error('❌ Error fetching suggestions:', err);
      setSuggestions([]);
    } finally {
      setLoadingSuggestions(false);
    }
  };

  // Fetch all customer data
  const fetchCustomerData = async () => {
    try {
      setLoading(true);
      setError(null);

      const [details, salesCogs, outstanding, categorySales] = await Promise.all([
        customerApi.getCustomerDetail(customerCode),
        customerApi.getSalesCogs(customerCode, salesYear),
        customerApi.getOutstandingBalance(customerCode),
        // customerApi.getCategorySales(customerCode, categoryYear),
      ]);

      setCustomerDetails(details);
      // setSalesCogsData(salesCogs || []);
      setOutstandingData(outstanding || []);
      // setCategorySalesData(categorySales || []);
    } catch (err) {
      console.error('❌ Error fetching customer data:', err);
      setError('Failed to load customer details');
    } finally {
      setLoading(false);
    }
  };

  // const fetchSalesCogs = async () => {
  //   try {
  //     const data = await customerApi.getSalesCogs(customerCode, salesYear);
  //     setSalesCogsData(data || []);
  //   } catch (err) {
  //     console.error('❌ Error fetching sales/COGS:', err);
  //   }
  // };
  const fetchSalesCogs = async (fy = salesFY, filters = {}) => {
  try {
    // Convert FY "2024-25" to startYear/endYear for the API
    const [startYearStr] = fy.split('-');
    const startYear = parseInt(startYearStr);
    const endYear   = startYear + 1;
    const data = await customerApi.getSalesCogsForFY(customerCode, startYear, endYear, filters);
    setSalesCogsData(data || []);
  } catch (err) {
    console.error('❌ Error fetching sales/COGS:', err);
  }
};

useEffect(() => {
  if (customerCode) fetchSalesCogs(salesFY, salesActiveFilters);
}, [customerCode, salesFY, salesActiveFilters]);

const handleSalesFiltersChange = useCallback((f) => setSalesActiveFilters(f), []);

  // const fetchCategorySales = async () => {
  //   try {
  //     const data = await customerApi.getCategorySales(customerCode, categoryYear);
  //     setCategorySalesData(data || []);
  //   } catch (err) {
  //     console.error('❌ Error fetching category sales:', err);
  //   }
  // };

  const fetchCategorySales = async (fy = categoryFY) => {
  try {
    const [startYearStr] = fy.split('-');
    const startYear = parseInt(startYearStr);
    const endYear   = startYear + 1;
    const data = await customerApi.getCategorySalesForFY(customerCode, startYear, endYear);
    setCategorySalesData(data || []);
  } catch (err) {
    console.error('❌ Error fetching category sales:', err);
  }
};

useEffect(() => {
  if (customerCode) fetchCategorySales(categoryFY);
}, [customerCode, categoryFY]);

  // Handle suggestion click
  const handleSuggestionClick = (customer) => {
    setSearchInput(customer.customerCode);
    setCustomerCode(customer.customerCode);
    setShowDropdown(false);
    setSuggestions([]);
  };

  // Handle search button click
  const handleSearch = () => {
    if (searchInput.trim()) {
      setCustomerCode(searchInput.trim());
      setShowDropdown(false);
    }
  };

  // Handle clear search
  const handleClearSearch = () => {
    setSearchInput('');
    setSuggestions([]);
    setShowDropdown(false);
  };

  // Outstanding balance filtering and pagination
  const getFilteredOutstanding = () => {
    if (!outstandingSearch.trim()) return outstandingData;
    
    const searchLower = outstandingSearch.toLowerCase();
    return outstandingData.filter(item =>
      item.invoiceNo?.toString().toLowerCase().includes(searchLower) ||
      item.customerCode?.toLowerCase().includes(searchLower) ||
      item.customerName?.toLowerCase().includes(searchLower) ||
      item.salesPerson?.toLowerCase().includes(searchLower)
    );
  };

  const getPaginatedOutstanding = () => {
    const filtered = getFilteredOutstanding();
    const start = outstandingPage * outstandingItemsPerPage;
    const end = start + outstandingItemsPerPage;
    return filtered.slice(start, end);
  };

  const outstandingTotalPages = Math.ceil(getFilteredOutstanding().length / outstandingItemsPerPage);

  // Excel Export for Outstanding Balance
  const handleExportOutstandingExcel = () => {
    try {
      if (!outstandingData || outstandingData.length === 0) {
        alert('No outstanding balance data to export');
        return;
      }

      const exportData = outstandingData.map(item => ({
        'Invoice No': item.invoiceNo,
        'Invoice Date': formatDate(item.invoiceDate),
        'SO#': item.so || 'N/A',
        'SO Date': formatDate(item.soDate),
        'Customer Code': item.customerCode,
        'Customer Name': item.customerName,
        'Contact Person': item.contactPerson || 'N/A',
        'SO Customer Ref No': item.soCustomerRefNo || 'N/A',
        'Invoice Total': item.invoiceTotal,
        'Balance Due': item.balanceDue,
        'Country': item.country || 'N/A',
        'State': item.state || 'N/A',
        'Overdue Days': item.overdueDays,
        'Payment Terms': item.paymentTerms || 'N/A',
        'Tracking No': item.trackingNo || 'N/A',
        'Dispatch Date': formatDate(item.dispatchDate),
        'Sales Person': item.salesPerson || 'N/A',
        'Payment Status': item.paymentStatus
      }));

      const worksheet = XLSX.utils.json_to_sheet(exportData);
      const workbook = XLSX.utils.book_new();
      XLSX.utils.book_append_sheet(workbook, worksheet, 'Outstanding Balance');
      XLSX.writeFile(workbook, `outstanding_balance_${customerCode}_${new Date().toISOString().split('T')[0]}.xlsx`);
      
      console.log('✅ Exported', exportData.length, 'outstanding records to Excel');
    } catch (err) {
      console.error('❌ Error exporting outstanding balance:', err);
      alert('Failed to export data');
    }
  };

  // Utility functions
  const formatCurrency = (amount) => {
    return '₹' + (amount || 0).toLocaleString('en-IN', { 
      minimumFractionDigits: 2, 
      maximumFractionDigits: 2 
    });
  };

  const formatDate = (dateString) => {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-GB', { 
      day: '2-digit', 
      month: 'short', 
      year: 'numeric' 
    });
  };

  const getYearOptions = () => {
    const currentYear = new Date().getFullYear();
    const years = [];
    for (let year = 2024; year <= currentYear; year++) {
      years.push(year);
    }
    return years;
  };

  // Prepare Sales/COGS chart data
  const getSalesCogsChartData = () => {
    const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
    const salesByMonth = new Array(12).fill(0);
    const cogsByMonth = new Array(12).fill(0);
    const gmPctByMonth = new Array(12).fill(0);

    salesCogsData.forEach(item => {
      const monthIndex = item.monthNumber - 1;
      if (monthIndex >= 0 && monthIndex < 12) {
        salesByMonth[monthIndex] = item.sales || 0;
        cogsByMonth[monthIndex] = item.cogs || 0;
        gmPctByMonth[monthIndex] = item.grossMarginPct || 0;
      }
    });

    return {
      labels: monthNames,
      datasets: [
        {
          type: 'bar',
          label: 'Sales (₹)',
          data: salesByMonth,
          backgroundColor: '#55A630',
          borderColor: '#2F6B1F',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          type: 'bar',
          label: 'COGS (₹)',
          data: cogsByMonth,
          backgroundColor: '#dc2626',
          borderColor: '#991b1b',
          borderWidth: 1,
          yAxisID: 'y',
        },
        {
          type: 'line',
          label: 'GM%',
          data: gmPctByMonth,
          borderColor: '#2563eb',
          backgroundColor: 'rgba(37, 99, 235, 0.1)',
          borderWidth: 2,
          yAxisID: 'y1',
          tension: 0.4,
        },
      ],
    };
  };

  const salesCogsChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            let label = context.dataset.label || '';
            if (label) {
              label += ': ';
            }
            if (context.dataset.yAxisID === 'y1') {
              label += context.parsed.y.toFixed(2) + '%';
            } else {
              label += formatCurrency(context.parsed.y);
            }
            return label;
          }
        }
      }
    },
    scales: {
      y: {
        type: 'linear',
        position: 'left',
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount (₹)'
        },
        ticks: {
          callback: function(value) {
            return '₹' + value.toLocaleString('en-IN');
          }
        }
      },
      y1: {
        type: 'linear',
        position: 'right',
        beginAtZero: true,
        title: {
          display: true,
          text: 'GM%'
        },
        ticks: {
          callback: function(value) {
            return value + '%';
          }
        },
        grid: {
          drawOnChartArea: false,
        },
      },
      x: {
        grid: {
          display: false,
        }
      }
    }
  };

  const getCategorySalesChartData = () => {
    console.log('📊 categorySalesData at chart render:', categorySalesData.length, categorySalesData.slice(0,3));
    const fyMonthLabels = [
        'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep',
        'Oct', 'Nov', 'Dec', 'Jan', 'Feb', 'Mar'
    ];

    // Apr(4)→0, May(5)→1 ... Dec(12)→8, Jan(1)→9, Feb(2)→10, Mar(3)→11
    const monthToFYIndex = (monthNum) => {
        if (monthNum >= 4) return monthNum - 4;
        return monthNum + 8;
    };

    const categories = [...new Set(categorySalesData.map(item => item.category))];

    const datasets = categories.map((category) => {
        const salesByFYSlot = new Array(12).fill(0);

        categorySalesData
            .filter(item => item.category === category)
            .forEach(item => {
                const slotIndex = monthToFYIndex(item.monthNumber);
                if (slotIndex >= 0 && slotIndex < 12) {
                    salesByFYSlot[slotIndex] += (item.sales || 0);
                }
            });

        const color = categoryColors[category] || categoryColors['Uncategorized'];

        return {
            label:           category,
            data:            salesByFYSlot,
            backgroundColor: color,
            borderColor:     color,
            borderWidth:     1,
        };
    });

    return { labels: fyMonthLabels, datasets };
};

  const categorySalesChartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        position: 'top',
      },
      tooltip: {
        callbacks: {
          label: function(context) {
            return context.dataset.label + ': ' + formatCurrency(context.parsed.y);
          }
        }
      }
    },
    scales: {
      x: {
        stacked: true,
        grid: {
          display: false,
        }
      },
      y: {
        stacked: true,
        beginAtZero: true,
        title: {
          display: true,
          text: 'Amount (₹)'
        },
        ticks: {
          callback: function(value) {
            return '₹' + value.toLocaleString('en-IN');
          }
        }
      }
    }
  };

  // Render component
  return (
    <div className={styles.wrapper}>
      {/* Search Bar */}
      <div className={styles.searchSection}>
        <div className={styles.searchRow}>
          <div className={styles.searchInputWrapper} ref={dropdownRef}>
            <span className={styles.searchIcon}>🔍</span>
            <input
              type="text"
              placeholder="Search by Customer Code or Name..."
              value={searchInput}
              onChange={(e) => setSearchInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
              onFocus={() => searchInput.trim().length >= 2 && setShowDropdown(true)}
              className={styles.searchInput}
            />
            {searchInput && (
              <button onClick={handleClearSearch} className={styles.clearSearchButton} title="Clear search">
                ✕
              </button>
            )}
            
            {showDropdown && (
              <div className={styles.dropdownMenu}>
                {loadingSuggestions ? (
                  <div className={styles.dropdownLoading}>Searching...</div>
                ) : suggestions.length > 0 ? (
                  suggestions.map((customer, index) => (
                    <div
                      key={index}
                      className={`${styles.dropdownItem} ${customer.customerCode === customerCode ? styles.dropdownItemActive : ''}`}
                      onClick={() => handleSuggestionClick(customer)}
                    >
                      <div className={styles.dropdownItemName}>{customer.customerName}</div>
                      <div className={styles.dropdownItemCode}>{customer.customerCode}</div>
                    </div>
                  ))
                ) : (
                  <div className={styles.dropdownNoResults}>No customers found</div>
                )}
              </div>
            )}
          </div>
          <button onClick={handleSearch} disabled={!searchInput.trim()} className={styles.searchButton}>
            Search
          </button>
        </div>
      </div>

      {loading && (
        <div className={styles.loadingContainer}>
          <div className={styles.loadingSpinner} />
          <div className={styles.loadingText}>Loading customer details...</div>
        </div>
      )}

      {error && (
        <div className={styles.errorMessage}>{error}</div>
      )}

      {customerDetails && !loading && (
        <>
          {/* Card 1: Customer Details */}
         {/* Card 1: Customer Details - COMPACT */}
          <div className={styles.card}>
            <h2 className={styles.cardHeader}>
              {customerDetails.customerName || 'N/A'}
            </h2>

            <div className={styles.compactGrid}>
              {[
                ['Customer Code',   customerDetails.customerCode],
                ['Customer Name',   customerDetails.customerName],
                ['Contact Person',  customerDetails.contactPerson],
                ['Phone',           customerDetails.phone],
                ['Email',           customerDetails.email],
                ['Balance',         formatCurrency(customerDetails.balance)],
                ['Currency',        customerDetails.currency],
                ['Credit Line',     formatCurrency(customerDetails.creditLine)],
                ['Sales Person',    customerDetails.salesEmployeeName],
              ].map(([label, val]) => (
                <div key={label} className={styles.compactRow}>
                  <span className={styles.compactLabel}>{label}</span>
                  <span className={styles.compactValue}>{val || 'N/A'}</span>
                </div>
              ))}
            </div>

            {customerDetails.notes && (
              <div className={styles.notesBox} style={{ marginTop: '12px' }}>
                <strong>Notes:</strong> {customerDetails.notes}
              </div>
            )}
          </div>

          {/* Card 2: Sales & COGS Analysis - ROTATED TABLE */}
          <div className={styles.card}>
  {/* Header */}
  <div className={styles.chartHeaderNoBorder}>
    <h2 className={styles.cardHeaderOnly}>Sales & COGS Analysis</h2>
    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      <label className={styles.yearLabel}>FY:</label>
      <select
        value={salesFY}
        onChange={(e) => setSalesFY(e.target.value)}
        className={styles.yearSelect}
      >
        {fyOptions.map(fy => <option key={fy} value={fy}>{fy}</option>)}
      </select>
    </div>
  </div>

  {/* Same filters as SalesVsCogs */}
  <SalesCogsFilters
    userRoleId={userRole ?? 3}
    onFiltersChange={handleSalesFiltersChange}
  />

  {/* Chart — exact same datasets/colors as SalesVsCogs */}
  {salesCogsData.length > 0 && (() => {
    const labels   = salesCogsData.map(d => d.month);
    const datasets = [
      {
        label: 'Lines',
        data: salesCogsData.map(d => d.lines),
        backgroundColor: '#06b6d4',
        type: 'bar', yAxisID: 'y2', order: 4
      },
      {
        label: 'Sales',
        data: salesCogsData.map(d => d.sales),
        backgroundColor: '#1e40af',
        type: 'bar', yAxisID: 'y', order: 3
      },
      {
        label: 'Order Value',
        data: salesCogsData.map(d => d.orderValue),
        backgroundColor: '#f97316',
        type: 'bar', yAxisID: 'y', order: 2
      },
      {
        label: 'COGS',
        data: salesCogsData.map(d => d.cogs),
        backgroundColor: '#16a34a',
        type: 'bar', yAxisID: 'y', order: 1
      },
      {
        label: 'GM%',
        data: salesCogsData.map(d => d.grossMarginPct),
        borderColor: '#4ade80',
        backgroundColor: 'rgba(74,222,128,0.1)',
        borderWidth: 3, type: 'line', yAxisID: 'y1',
        tension: 0.4, pointRadius: 5, pointHoverRadius: 7,
        pointBackgroundColor: '#4ade80', pointBorderColor: '#ffffff',
        pointBorderWidth: 2, fill: true, order: 0
      }
    ];

    const options = {
      responsive: true, maintainAspectRatio: false,
      interaction: { mode: 'index', intersect: false },
      plugins: {
        legend: { position: 'top', labels: { usePointStyle: true, padding: 15, font: { size: 12 } } },
        tooltip: {
          callbacks: {
            label: (ctx) => {
              const label = ctx.dataset.label + ': ';
              if (ctx.dataset.yAxisID === 'y1') return label + ctx.parsed.y.toFixed(2) + '%';
              if (ctx.dataset.yAxisID === 'y2') return label + ctx.parsed.y.toLocaleString('en-IN');
              return label + '₹' + ctx.parsed.y.toLocaleString('en-IN');
            }
          }
        }
      },
      scales: {
        x: { grid: { display: false } },
        y: {
          type: 'linear', position: 'left',
          title: { display: true, text: 'Amount (₹)', font: { size: 12, weight: 'bold' } },
          ticks: { callback: (v) => '₹' + (v / 10000000).toFixed(1) + 'Cr' },
          grid: { color: 'rgba(0,0,0,0.05)' }
        },
        y1: {
          type: 'linear', position: 'right',
          title: { display: true, text: 'GM %', font: { size: 12, weight: 'bold' } },
          min: 0, max: 35,
          ticks: { callback: (v) => v + '%', stepSize: 5 },
          grid: { drawOnChartArea: false }
        },
        y2: {
          type: 'linear', position: 'right',
          title: { display: true, text: 'Lines', font: { size: 12, weight: 'bold' } },
          min: 0,
          ticks: { stepSize: 500 },
          grid: { drawOnChartArea: false }
        }
      }
    };

    const fmt  = (n) => '₹' + (n || 0).toLocaleString('en-IN', { minimumFractionDigits: 0 });
    const fmtP = (n) => (n || 0).toFixed(2) + '%';
    const fmtN = (n) => (n || 0).toLocaleString('en-IN');
    const total = (f) => salesCogsData.reduce((s, d) => s + (Number(d[f]) || 0), 0);
    const totalS = total('sales'), totalC = total('cogs');
    const totalGM = totalS === 0 ? 0 : ((totalS - totalC) * 100) / totalS;

    return (
      <>
        <div className={styles.chartContainer}>
          <Chart type="bar" data={{ labels, datasets }} options={options} />
        </div>
        <div className={styles.tableWrapper} style={{ overflowX: 'auto', marginTop: '16px' }}>
          <table className={styles.table} style={{ minWidth: '900px', fontSize: '13px' }}>
            <thead>
              <tr>
                <th className={styles.th}>Metric</th>
                {salesCogsData.map((d, i) => <th key={i} className={styles.th}>{d.month}</th>)}
                <th className={styles.th}>Total</th>
              </tr>
            </thead>
            <tbody>
              {[
                ['Sales',       (d) => fmt(d.sales),         fmt(total('sales'))],
                ['COGS',        (d) => fmt(d.cogs),          fmt(total('cogs'))],
                ['GM %',        (d) => fmtP(d.grossMarginPct), fmtP(totalGM)],
                ['Lines',       (d) => fmtN(d.lines),        fmtN(total('lines'))],
                ['Order Value', (d) => fmt(d.orderValue),    fmt(total('orderValue'))],
              ].map(([label, cellFn, totalVal]) => (
                <tr key={label}>
                  <td className={styles.tdHeader}>{label}</td>
                  {salesCogsData.map((d, i) => <td key={i} className={styles.td}>{cellFn(d)}</td>)}
                  <td className={styles.tdTotal}>{totalVal}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </>
    );
  })()}
</div>

          {/* Card 3: Outstanding Balance with Search, Pagination & Excel Export */}
          <div className={styles.card}>
            <div className={styles.chartHeaderNoBorder}>
                  <h2 className={styles.cardHeaderOnly}>Outstanding Balance</h2>
                  <div style={{ display: 'flex', gap: '8px', alignItems: 'center' }}>
                    <select
                      value={outstandingFilter}
                      onChange={(e) => setOutstandingFilter(e.target.value)}
                      className={styles.yearSelect}
                    >
                      <option value="Payment Pending">Payment Pending</option>
                      <option value="Payment Done">Payment Done</option>
                    </select>
                    <button
                      onClick={handleMailSend}
                      disabled={isMailSending || selectedInvoices.length === 0}
                      style={{ padding: '8px 16px', background: '#2563eb', color: 'white',
                              border: 'none', borderRadius: '6px', cursor: 'pointer', fontSize: '0.875rem' }}
                    >
                      {isMailSending ? 'Sending...' : `Mail (${selectedInvoices.length})`}
                    </button>
                    <button onClick={handleExportOutstandingExcel}
                      disabled={outstandingData.length === 0}
                      className={styles.excelButton}>
                      📊 Export Excel
                    </button>
                  </div>
                </div>
            
            {/* Search for outstanding */}
            <div className={styles.outstandingSearchRow}>
              <div className={styles.searchInputWrapper}>
                <span className={styles.searchIcon}>🔍</span>
                <input
                  type="text"
                  placeholder="Search outstanding..."
                  value={outstandingSearch}
                  onChange={(e) => {
                    setOutstandingSearch(e.target.value);
                    setOutstandingPage(0);
                  }}
                  className={styles.searchInput}
                />
                {outstandingSearch && (
                  <button 
                    onClick={() => {
                      setOutstandingSearch('');
                      setOutstandingPage(0);
                    }} 
                    className={styles.clearSearchButton}
                  >
                    ✕
                  </button>
                )}
              </div>
            </div>

            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    <th className={styles.th}>
                      <input type="checkbox"
                        checked={selectedInvoices.length === outstandingData.length && outstandingData.length > 0}
                        onChange={(e) => setSelectedInvoices(
                          e.target.checked ? outstandingData.map(r => r.invoiceNo) : []
                        )}
                      />
                    </th>
                   
                    <th className={styles.th}>Invoice No</th>
                    <th className={styles.th}>Invoice Date</th>
                    <th className={styles.th}>SO#</th>
                    <th className={styles.th}>SO Date</th>
                    <th className={styles.th}>Customer Code</th>
                    <th className={styles.th}>Customer Name</th>
                    <th className={styles.th}>Contact Person</th>
                    <th className={styles.th}>SO Customer Ref No</th>
                    <th className={styles.th}>Invoice Total</th>
                    <th className={styles.th}>Balance Due</th>
                    <th className={styles.th}>Country</th>
                    <th className={styles.th}>State</th>
                    <th className={styles.th}>Overdue Days</th>
                    <th className={styles.th}>Payment Terms</th>
                    <th className={styles.th}>Tracking No</th>
                    <th className={styles.th}>Dispatch Date</th>
                    <th className={styles.th}>Sales Person</th>
                    <th className={styles.th}>Payment Status</th>
                  </tr>
                </thead>
                <tbody>
                  {getPaginatedOutstanding().length > 0 ? (
                    getPaginatedOutstanding().map((item, index) => (
                      <tr key={index} className={styles.tr}
                        style={{ background: selectedInvoices.includes(item.invoiceNo) ? '#eff6ff' : '' }}>
                          <td className={styles.td}>
                            <input type="checkbox"
                              checked={selectedInvoices.includes(item.invoiceNo)}
                              onChange={(e) => setSelectedInvoices(prev =>
                                e.target.checked
                                  ? [...prev, item.invoiceNo]
                                  : prev.filter(id => id !== item.invoiceNo)
                              )}
                            />
                          </td>
                        <td className={styles.td}>{item.invoiceNo}</td>
                        <td className={styles.td}>{formatDate(item.invoiceDate)}</td>
                        <td className={styles.td}>{item.so || 'N/A'}</td>
                        <td className={styles.td}>{formatDate(item.soDate)}</td>
                        <td className={styles.td}>{item.customerCode}</td>
                        <td className={styles.td}>{item.customerName}</td>
                        <td className={styles.td}>{item.contactPerson || 'N/A'}</td>
                        <td className={styles.td}>{item.soCustomerRefNo || 'N/A'}</td>
                        <td className={styles.td}>{formatCurrency(item.invoiceTotal)}</td>
                        <td className={styles.td}>{formatCurrency(item.balanceDue)}</td>
                        <td className={styles.td}>{item.country || 'N/A'}</td>
                        <td className={styles.td}>{item.state || 'N/A'}</td>
                        <td className={styles.td}>{item.overdueDays} days</td>
                        <td className={styles.td}>{item.paymentTerms || 'N/A'}</td>
                        <td className={styles.td}>{item.trackingNo || 'N/A'}</td>
                        <td className={styles.td}>{formatDate(item.dispatchDate)}</td>
                        <td className={styles.td}>{item.salesPerson || 'N/A'}</td>
                        <td className={styles.td}>
                          <span className={`${styles.statusBadge} ${item.paymentStatus === 'Pending' ? styles.statusPending : styles.statusReceived}`}>
                            {item.paymentStatus}
                          </span>
                        </td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td className={styles.td} colSpan="18" style={{ textAlign: 'center' }}>
                        {outstandingSearch ? 'No matching records found' : 'No outstanding balance'}
                      </td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>

            {/* Outstanding Pagination */}
            {getFilteredOutstanding().length > outstandingItemsPerPage && (
              <div className={styles.paginationSection}>
                <div className={styles.paginationInfo}>
                  Showing {outstandingPage * outstandingItemsPerPage + 1} - {Math.min((outstandingPage + 1) * outstandingItemsPerPage, getFilteredOutstanding().length)} of {getFilteredOutstanding().length} records
                </div>
                <div className={styles.paginationControls}>
                  <button
                    onClick={() => setOutstandingPage(0)}
                    disabled={outstandingPage === 0}
                    className={styles.paginationButton}
                  >
                    First
                  </button>
                  <button
                    onClick={() => setOutstandingPage(prev => Math.max(0, prev - 1))}
                    disabled={outstandingPage === 0}
                    className={styles.paginationButton}
                  >
                    Prev
                  </button>
                  <div className={styles.pageNumber}>{outstandingPage + 1} / {outstandingTotalPages}</div>
                  <button
                    onClick={() => setOutstandingPage(prev => Math.min(outstandingTotalPages - 1, prev + 1))}
                    disabled={outstandingPage === outstandingTotalPages - 1}
                    className={styles.paginationButton}
                  >
                    Next
                  </button>
                  <button
                    onClick={() => setOutstandingPage(outstandingTotalPages - 1)}
                    disabled={outstandingPage === outstandingTotalPages - 1}
                    className={styles.paginationButton}
                  >
                    Last
                  </button>
                </div>
              </div>
            )}
          </div>

          {/* Card 4: Category Sales */}
          <div className={styles.card}>
           <div className={styles.chartHeaderNoBorder}>
            <h2 className={styles.cardHeaderOnly}>Category Sales</h2>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <label className={styles.yearLabel}>FY:</label>
              <select value={categoryFY} onChange={(e) => setCategoryFY(e.target.value)} className={styles.yearSelect}>
                {fyOptions.map(fy => <option key={fy} value={fy}>{fy}</option>)}
              </select>
            </div>
          </div>
            <div className={styles.chartContainer}>
              <Chart type="bar" data={getCategorySalesChartData()} options={categorySalesChartOptions} />
            </div>
          </div>

          {/* Card 5: Addresses */}
          {customerDetails.addresses && customerDetails.addresses.length > 0 && (
          <div className={styles.card}>
            <h2 className={styles.cardHeaderOnly} style={{ marginBottom: '16px' }}>Addresses</h2>
            <div className={styles.tableWrapper}>
              <table className={styles.table}>
                <thead>
                  <tr>
                    {['Type','Address Name','Street','Block','City','State','Zip Code','Country']
                      .map(h => <th key={h} className={styles.th}>{h}</th>)}
                  </tr>
                </thead>
                <tbody>
                  {customerDetails.addresses.map((addr, i) => (
                    <tr key={i} className={styles.tr}>
                      <td className={styles.td}>
                        {addr.addressType === 'B' ? 'Billing' : 'Shipping'}
                      </td>
                      <td className={styles.td}>{addr.addressName || 'N/A'}</td>
                      <td className={styles.td}>{addr.street      || 'N/A'}</td>
                      <td className={styles.td}>{addr.block       || 'N/A'}</td>
                      <td className={styles.td}>{addr.city        || 'N/A'}</td>
                      <td className={styles.td}>{addr.state       || 'N/A'}</td>
                      <td className={styles.td}>{addr.zipCode     || 'N/A'}</td>
                      <td className={styles.td}>{addr.country     || 'N/A'}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
          )}
        </>
      )}

      {!customerDetails && !loading && (
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>👤</div>
          <div className={styles.emptyText}>Search for a customer to view details</div>
        </div>
      )}
    </div>
  );
};

export default CustomerDetail;